
import { Clock, Calendar, CheckCircle, SkipForward, XCircle } from 'lucide-react';
import { PACT } from '@/types/pact';
import { useState } from 'react';
import ConfettiEffect from './ConfettiEffect';

interface PACTCardProps {
  pact: PACT;
  streak: number;
  isCompleted: boolean;
  onToggle: () => void;
  onCardClick: () => void;
  onSkip?: () => void;
  onShowBanner?: (message: string, actionType: 'now-completed' | 'now-skipped' | 'now-missed') => void;
}

const PACTCard = ({ pact, streak, isCompleted, onToggle, onCardClick, onSkip, onShowBanner }: PACTCardProps) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiType, setConfettiType] = useState<'complete' | 'skip' | 'missed'>('complete');
  const [isAnimating, setIsAnimating] = useState(false);
  
  const createdDate = new Date(pact.createdAt);
  const daysSinceCreated = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
  const daysRemaining = Math.max(0, pact.duration - daysSinceCreated);
  const completionRate = Math.round((pact.completedDays.length / Math.max(1, daysSinceCreated + 1)) * 100);
  
  const today = new Date().toISOString().split('T')[0];
  const isSkipped = pact.skippedDays?.includes(today) || false;
  const skipsRemaining = (pact.maxSkips || 0) - (pact.skippedDays?.length || 0);

  const handleCardClick = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget && !(e.target as Element).closest('.pact-card-content')) {
      return;
    }
    onCardClick();
  };

  const handleComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Add zoom animation to button
    const button = e.currentTarget as HTMLElement;
    button.style.transform = 'scale(0.9)';
    setTimeout(() => {
      button.style.transform = 'scale(1)';
    }, 150);
    
    setConfettiType('complete');
    setShowConfetti(true);
    onToggle();
    onShowBanner?.("", 'now-completed');
  };

  const handleSkip = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Check if we can skip first
    if (skipsRemaining <= 0) {
      console.log('🚫 No skips remaining, action blocked');
      return;
    }
    
    console.log('🎯 Skip button clicked - executing all actions');
    
    // Add zoom animation
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 150);
    
    // Reset and trigger confetti
    setShowConfetti(false);
    setTimeout(() => {
      setConfettiType('skip');
      setShowConfetti(true);
      console.log('🎊 Confetti triggered for skip');
    }, 50);
    
    // Execute skip action
    if (onSkip) {
      onSkip();
      console.log('✅ onSkip executed');
    }
    
    // Show banner
    if (onShowBanner) {
      onShowBanner("", 'now-skipped');
      console.log('📢 Banner triggered for skip');
    }
  };

  const handleMissed = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Add zoom animation to button
    const button = e.currentTarget as HTMLElement;
    button.style.transform = 'scale(0.9)';
    setTimeout(() => {
      button.style.transform = 'scale(1)';
    }, 150);
    
    setConfettiType('missed');
    setShowConfetti(true);
    if (isCompleted) {
      onToggle();
    } else if (isSkipped && onSkip) {
      onSkip();
    }
    onShowBanner?.("", 'now-missed');
  };

  const getActionStatus = () => {
    if (isCompleted) return 'completed';
    if (isSkipped) return 'skipped';
    return 'pending';
  };

  return (
    <>
      <div className="pact-card cursor-pointer relative transform transition-all duration-300 hover:scale-[1.02]" onClick={handleCardClick}>
        <div className="pact-card-content flex items-center justify-between">
          {/* Content Section - Left Side */}
          <div className="flex-1 pr-4">
            <h3 className="text-lg font-semibold text-deep-blue mb-2">{pact.name}</h3>
            
            <div className="flex items-center gap-4 text-sm text-deep-blue/70 mb-2">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{daysRemaining} days left</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{completionRate}% done</span>
              </div>
            </div>
            
            <div className="text-xs text-gray-500 mb-2">
              Status: {getActionStatus()} | Skips: {skipsRemaining}
            </div>
            
            {streak > 0 && (
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-vibrant-orange/10 text-vibrant-orange rounded-full text-sm font-medium">
                🔥 {streak} day streak
              </div>
            )}
          </div>

          {/* Action Buttons - Right Side Vertical */}
          <div className="flex flex-col gap-2">
            <button
              onClick={handleMissed}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 transition-all duration-200 hover:scale-110 active:scale-95"
              title="Mark as missed"
            >
              <XCircle className="w-3 h-3" />
            </button>
            
            <button
              onClick={handleSkip}
              className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 ${
                skipsRemaining > 0 
                  ? 'bg-yellow-50 hover:bg-yellow-100 text-yellow-600 border border-yellow-200 hover:scale-110 active:scale-95' 
                  : 'bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed'
              } ${isAnimating ? 'scale-90' : 'scale-100'}`}
              title={skipsRemaining > 0 ? "Skip today" : "No skips remaining"}
              disabled={skipsRemaining <= 0}
            >
              <SkipForward className="w-3 h-3" />
            </button>
            
            <button
              onClick={handleComplete}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-green-50 hover:bg-green-100 text-green-600 border border-green-200 transition-all duration-200 hover:scale-110 active:scale-95"
              title="Mark as complete"
            >
              <CheckCircle className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
      
      <ConfettiEffect
        trigger={showConfetti}
        type={confettiType}
        onComplete={() => setShowConfetti(false)}
      />
    </>
  );
};

export default PACTCard;
